import { activeLines, contentText, reservedByArticle } from "@lagerlog/core";
import type { ReservationLine } from "@lagerlog/core";
import type { Article, Catalog } from "../catalog.ts";
import { takeWords } from "../brand.ts";
import { useStore } from "../hooks.ts";

/** Today in German time, as reservations count their days. */
export const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });

/** „Do 24.10.“ */
const weekday = (day: string) => {
  const d = new Date(`${day}T12:00:00`);
  return `${d.toLocaleDateString("de-DE", { weekday: "short" }).replace(".", "")} ${day.slice(8, 10)}.${day.slice(5, 7)}.`;
};

/** The reservations that hold today, per article (docs/18-vorratsbuch.md, V3). */
export function reservedOf(catalog: Catalog): { lines: (ReservationLine & { plan: string })[]; byArticle: Map<string, number> } {
  const lines = activeLines(catalog.reservations, today());
  return { lines, byArticle: reservedByArticle(lines) };
}

/** In the base unit, never in packs: 500 g of a 700 g bottle are no „opened bottle“ here. */
const amount = (a: Article, qty: number) => contentText(qty, a.unit);

/**
 * „Vorgemerkt für Risotto (Do 24.10.) 375 ml“ at an article, with what stays available. A reservation
 * changes no stock; taking it out is not blocked.
 */
export function Reserved({ a, catalog, stock }: { a: Article; catalog: Catalog; stock: number }) {
  const { store } = useStore();
  const lines = reservedOf(catalog).lines.filter((l) => l.article === a.id);
  if (lines.length === 0) return null;
  const held = lines.reduce((s, l) => s + l.reserved, 0);
  return (
    <div className="card" data-testid="reserved">
      <strong>Vorgemerkt</strong>
      <ul className="detail">
        {lines
          .sort((x, y) => x.day.localeCompare(y.day))
          .map((l) => (
            <li key={`${l.plan}|${l.recipe.id}|${l.day}`} className="row between">
              <span>
                für {l.recipe.title} ({weekday(l.day)})
              </span>
              <span>{amount(a, l.reserved)}</span>
            </li>
          ))}
        <li className="row between">
          <strong>verfügbar</strong>
          <strong data-testid="available">{amount(a, Math.max(0, stock - held))}</strong>
        </li>
      </ul>
      <p className="hint">Vorgemerkt von SpeisePlan. {takeWords(store.config?.kind).take} geht trotzdem; nach dem Kochtag verfällt eine Vormerkung nach 2 Tagen.</p>
    </div>
  );
}

/** At the booking: what of this article is held for which recipe. */
export function ReservedHint({ a, catalog }: { a: Article; catalog: Catalog }) {
  const lines = reservedOf(catalog).lines.filter((l) => l.article === a.id);
  if (lines.length === 0) return null;
  return (
    <p className="notice" data-testid="reserved-hint">
      Vorgemerkt für {lines.map((l) => `${l.recipe.title} (${weekday(l.day)})`).join(", ")}.
    </p>
  );
}
